import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { createPublicApi } from '../services/api';
import ReceiptGenerator from '../components/common/ReceiptGenerator';

const STATUS_STEPS = ['pending', 'confirmed', 'preparing', 'ready', 'delivered'];

const STATUS_INFO = {
  pending:   { icon: '⏳', label: 'Order Received',  color: '#f59e0b', desc: 'Your order has been received and is awaiting confirmation.' },
  confirmed: { icon: '✅', label: 'Order Confirmed', color: '#3b82f6', desc: 'Your order has been confirmed by our team.' },
  preparing: { icon: '👨‍🍳', label: 'Being Prepared', color: '#f97316', desc: 'Our chefs are preparing your order with love.' },
  ready:     { icon: '🛎️', label: 'Ready to Serve',  color: '#8b5cf6', desc: 'Your order is ready! It will be served shortly.' },
  delivered: { icon: '🎉', label: 'Delivered',        color: '#22c55e', desc: 'Your order has been delivered. Enjoy your meal!' },
  cancelled: { icon: '❌', label: 'Cancelled',        color: '#ef4444', desc: 'This order has been cancelled.' },
};

const inputStyle = {
  width: '100%',
  padding: '15px 18px',
  borderRadius: 14,
  border: '1px solid rgba(201,100,40,0.25)',
  background: 'rgba(255,255,255,0.07)',
  color: '#f5ede0',
  fontSize: 15,
  outline: 'none',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

// ── Order History Card ─────────────────────────────────────────────────────
const HistoryCard = ({ order, onViewDetail }) => {
  const si = STATUS_INFO[order.status] || STATUS_INFO.pending;
  const formatDate = (d) => new Date(d).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      style={{
        background: 'white', borderRadius: 18, padding: '20px 22px',
        border: '1px solid #f0e6db', boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
        display: 'flex', flexDirection: 'column', gap: 14,
      }}
    >
      {/* Top row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }}>{si.icon}</span>
          <div>
            <p style={{ fontWeight: 800, color: '#c96428', fontSize: 16 }}>Order #{order.order_number || order.id}</p>
            <p style={{ color: '#8b6347', fontSize: 12, marginTop: 2 }}>{formatDate(order.created_at)}</p>
          </div>
        </div>
        <span style={{
          fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 99,
          background: `${si.color}15`, color: si.color, border: `1px solid ${si.color}30`,
        }}>
          {si.label}
        </span>
      </div>

      {/* Items preview */}
      <div style={{ background: '#fdf6f0', borderRadius: 12, padding: '10px 14px' }}>
        <p style={{ color: '#8b6347', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>Items</p>
        {(order.items || []).slice(0, 3).map((item, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#3d2010', padding: '3px 0' }}>
            <span>{item.item_name} × {item.quantity}</span>
            <span style={{ color: '#c96428', fontWeight: 600 }}>₹{parseFloat(item.subtotal).toFixed(0)}</span>
          </div>
        ))}
        {order.items.length > 3 && (
          <p style={{ color: '#a07050', fontSize: 12, marginTop: 4 }}>+{order.items.length - 3} more item(s)</p>
        )}
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <p style={{ color: '#8b6347', fontSize: 11 }}>
            {(order.order_type || 'dine-in').replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </p>
          <p style={{ fontWeight: 800, color: '#c96428', fontSize: 18 }}>₹{parseFloat(order.total_amount).toFixed(0)}</p>
        </div>
        <button onClick={() => onViewDetail(order)}
          style={{
            padding: '10px 20px', borderRadius: 12, border: 'none', cursor: 'pointer',
            background: 'linear-gradient(135deg, #c96428, #a84e1e)',
            color: 'white', fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
            boxShadow: '0 3px 10px rgba(201,100,40,0.3)',
          }}>
          View Details →
        </button>
      </div>
    </motion.div>
  );
};

// ── Order Detail Modal ─────────────────────────────────────────────────────
const OrderDetailModal = ({ order, gstSettings, onClose, slug }) => {
  const [showReceipt, setShowReceipt] = useState(false);
  const si = STATUS_INFO[order.status] || STATUS_INFO.pending;
  const currentStepIndex = STATUS_STEPS.indexOf(order.status);

  const formatDate = (d) => new Date(d).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });

  const itemsTotal = (order.items || []).reduce((s, i) => s + parseFloat(i.subtotal || i.price * i.quantity), 0);
  const discount = parseFloat(order.discount_amount || 0);
  const afterDiscount = Math.max(itemsTotal - discount, 0);
  const tax = gstSettings.enabled && gstSettings.rate > 0 ? (afterDiscount * gstSettings.rate) / 100 : 0;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
      onClick={onClose}
    >
      <motion.div initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }}
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fdfaf6', borderRadius: 24, width: '100%', maxWidth: 540,
          maxHeight: '90vh', overflowY: 'auto', padding: 28,
          boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 className="font-display" style={{ fontSize: 22, fontWeight: 800, color: '#1a0f00' }}>
            Order #{order.order_number || order.id}
          </h3>
          <button onClick={onClose} style={{
            background: 'white', cursor: 'pointer', fontSize: 18, color: '#8b6347',
            border: '1px solid #e2d5c8', borderRadius: '50%', width: 36, height: 36,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>×</button>
        </div>

        {/* Status Banner */}
        <div style={{
          borderRadius: 16, padding: '20px', textAlign: 'center', marginBottom: 16,
          background: `linear-gradient(135deg, ${si.color}12, ${si.color}06)`,
          border: `1px solid ${si.color}30`,
        }}>
          <div style={{ fontSize: 40, marginBottom: 6 }}>{si.icon}</div>
          <h4 style={{ fontSize: 18, fontWeight: 700, color: si.color, marginBottom: 4 }}>{si.label}</h4>
          <p style={{ color: '#8b6347', fontSize: 13 }}>{si.desc}</p>
        </div>

        {/* Progress Bar */}
        {order.status !== 'cancelled' && (
          <div style={{ background: 'white', borderRadius: 16, padding: '18px 20px', border: '1px solid #f0e6db', marginBottom: 16 }}>
            <p style={{ fontWeight: 700, color: '#1a0f00', marginBottom: 20, fontSize: 13 }}>Order Progress</p>
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', top: 18, left: '5%', right: '5%', height: 2, background: '#f0e6db', borderRadius: 2 }} />
              <div style={{
                position: 'absolute', top: 18, left: '5%', height: 2,
                background: 'linear-gradient(90deg, #c96428, #e07832)', borderRadius: 2,
                width: currentStepIndex >= 0 ? `${(currentStepIndex / (STATUS_STEPS.length - 1)) * 90}%` : '0%',
              }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative' }}>
                {STATUS_STEPS.map((step, i) => {
                  const done = i <= currentStepIndex;
                  const current = i === currentStepIndex;
                  return (
                    <div key={step} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: '50%', zIndex: 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: current ? 14 : 11, fontWeight: 700,
                        background: done ? '#c96428' : '#f5ede0',
                        color: done ? 'white' : '#b09070',
                        border: current ? '3px solid #c96428' : '2px solid #e2d5c8',
                        boxShadow: current ? '0 0 16px rgba(201,100,40,0.4)' : 'none',
                      }}>
                        {done ? (current ? STATUS_INFO[step]?.icon : '✓') : i + 1}
                      </div>
                      <span style={{ fontSize: 9, fontWeight: current ? 700 : 500, color: done ? '#c96428' : '#b09070', textAlign: 'center', maxWidth: 50 }}>
                        {STATUS_INFO[step]?.label.split(' ')[0]}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Info Grid */}
        <div style={{ background: 'white', borderRadius: 16, padding: '18px 20px', border: '1px solid #f0e6db', marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 18 }}>
            {[
              ['Customer', order.customer_name],
              ['Phone', order.phone],
              ['Order Type', (order.order_type || 'dine-in').replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())],
              ['Date', formatDate(order.created_at)],
            ].map(([label, value]) => (
              <div key={label} style={{ background: '#fdf6f0', borderRadius: 10, padding: '10px 12px' }}>
                <p style={{ color: '#8b6347', fontSize: 11, marginBottom: 2 }}>{label}</p>
                <p style={{ color: '#1a0f00', fontWeight: 600, fontSize: 13 }}>{value}</p>
              </div>
            ))}
          </div>

          {/* Items */}
          <div style={{ borderTop: '1px dashed #e2d5c8', paddingTop: 14 }}>
            <p style={{ color: '#8b6347', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Items Ordered</p>
            {(order.items || []).map((item, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #f5ede0' }}>
                <div>
                  <p style={{ color: '#1a0f00', fontWeight: 500, fontSize: 13 }}>{item.item_name}</p>
                  <p style={{ color: '#8b6347', fontSize: 11, marginTop: 1 }}>₹{parseFloat(item.price).toFixed(0)} × {item.quantity}</p>
                </div>
                <span style={{ color: '#c96428', fontWeight: 700, fontSize: 13 }}>₹{parseFloat(item.subtotal).toFixed(0)}</span>
              </div>
            ))}

            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '2px solid #c96428', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {discount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', borderRadius: 8, background: '#dcfce7', border: '1px solid #a5d6a7' }}>
                  <span style={{ color: '#16a34a', fontSize: 13, fontWeight: 700 }}>🎟️ {order.coupon_code || 'Discount'}</span>
                  <span style={{ color: '#16a34a', fontSize: 13, fontWeight: 700 }}>-₹{discount.toFixed(0)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b5344', fontSize: 13 }}>Subtotal</span>
                <span style={{ color: '#6b5344', fontSize: 13 }}>₹{afterDiscount.toFixed(0)}</span>
              </div>
              {tax > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6b5344', fontSize: 13 }}>{gstSettings.label} ({gstSettings.rate}%)</span>
                  <span style={{ color: '#c96428', fontSize: 13 }}>+₹{tax.toFixed(0)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px dashed #e0cfc4', marginTop: 4 }}>
                <span className="font-display" style={{ fontWeight: 700, color: '#1a0f00', fontSize: 16 }}>Total Paid</span>
                <span style={{ fontWeight: 700, color: '#c96428', fontSize: 16 }}>₹{(afterDiscount + tax).toFixed(0)}</span>
              </div>
            </div>
          </div>

          <button onClick={() => setShowReceipt(true)}
            style={{
              width: '100%', marginTop: 18, padding: '13px 24px', borderRadius: 12,
              border: 'none', cursor: 'pointer', fontFamily: 'inherit',
              background: 'linear-gradient(135deg, #c96428, #a84e1e)',
              color: 'white', fontSize: 14, fontWeight: 700,
              boxShadow: '0 4px 14px rgba(201,100,40,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}>
            📥 Download Receipt
          </button>
        </div>

        <AnimatePresence>
          {showReceipt && <ReceiptGenerator order={order} slug={slug} onClose={() => setShowReceipt(false)} />}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────
const TrackOrder = () => {
  const { slug } = useParams();
  const [tab, setTab] = useState('track'); // 'track' | 'history'

  // Track tab state
  const [orderId, setOrderId] = useState('');
  const [trackEmail, setTrackEmail] = useState('');
  const [order, setOrder] = useState(null);
  const [trackLoading, setTrackLoading] = useState(false);
  const [trackError, setTrackError] = useState('');
  const [showReceipt, setShowReceipt] = useState(false);

  // History tab state
  const [historyEmail, setHistoryEmail] = useState('');
  const [historyOrders, setHistoryOrders] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [historySearched, setHistorySearched] = useState(false);

  const [gstSettings, setGstSettings] = useState({ enabled: false, rate: 0, label: 'GST' });

  React.useEffect(() => {
    if (!slug) return;
    const pubApi = createPublicApi(slug);
    pubApi.settings.get().then(r => {
      const d = r.data.data || {};
      setGstSettings({
        enabled: d.gst_enabled === 'true',
        rate: parseFloat(d.gst_rate) || 0,
        label: d.gst_label || 'GST',
      });
    }).catch(() => {});
  }, [slug]);

  // Track single order
  const handleTrack = async (e) => {
    e.preventDefault();
    if (!orderId.trim() || !trackEmail.trim()) return;
    setTrackLoading(true);
    setTrackError('');
    setOrder(null);
    try {
      const pubApi = createPublicApi(slug);
      const res = await pubApi.orders.track({ order_id: orderId.trim(), email: trackEmail.trim() });
      setOrder(res.data.data);
    } catch (err) {
      setTrackError(err.response?.data?.message || 'No order found. Please check your Order ID and Email.');
    } finally {
      setTrackLoading(false);
    }
  };

  // Fetch order history
  const handleHistory = async (e) => {
    e.preventDefault();
    if (!historyEmail.trim()) return;
    setHistoryLoading(true);
    setHistoryError('');
    setHistoryOrders([]);
    setHistorySearched(true);
    try {
      const pubApi = createPublicApi(slug);
      const res = await pubApi.orders.history({ email: historyEmail.trim() });
      setHistoryOrders(res.data.data);
    } catch (err) {
      setHistoryError(err.response?.data?.message || 'No orders found for this email.');
    } finally {
      setHistoryLoading(false);
    }
  };

  const currentStepIndex = order ? STATUS_STEPS.indexOf(order.status) : -1;
  const statusInfo = order ? (STATUS_INFO[order.status] || STATUS_INFO.pending) : null;

  const formatDate = (d) => new Date(d).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });

  return (
    <div style={{ minHeight: '100vh', background: '#fdfaf6' }}>

      {/* ── Hero ── */}
      <div style={{
        background: 'linear-gradient(135deg, #0e0700 0%, #1e0f00 50%, #0e0700 100%)',
        paddingTop: 96, paddingBottom: 56,
      }}>
        <div style={{ maxWidth: 560, margin: '0 auto', padding: '0 20px', textAlign: 'center' }}>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '8px 18px', borderRadius: 99, marginBottom: 24,
              background: 'rgba(201,100,40,0.12)', border: '1px solid rgba(201,100,40,0.25)',
              color: '#e8a878', fontSize: 13, fontWeight: 500,
            }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#c96428', display: 'inline-block' }} />
            Real-time Order Tracking
          </motion.div>

          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="font-display"
            style={{ fontSize: 'clamp(2rem,5vw,3.2rem)', fontWeight: 700, color: '#f5ede0', lineHeight: 1.2, marginBottom: 12 }}>
            Track Your <span style={{ color: '#c96428' }}>Order</span>
          </motion.h1>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            style={{ color: '#6b4226', fontSize: 15, marginBottom: 36 }}>
            Track a single order or view your complete order history
          </motion.p>

          {/* Tabs */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
            style={{
              display: 'inline-flex', background: 'rgba(255,255,255,0.06)',
              borderRadius: 14, padding: 4, border: '1px solid rgba(201,100,40,0.2)',
            }}>
            {[
              { key: 'track', label: '🔍 Track Order' },
              { key: 'history', label: '📋 Order History' },
            ].map(t => (
              <button key={t.key} onClick={() => setTab(t.key)}
                style={{
                  padding: '10px 24px', borderRadius: 10, border: 'none',
                  cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 600,
                  background: tab === t.key ? 'linear-gradient(135deg, #c96428, #a84e1e)' : 'transparent',
                  color: tab === t.key ? 'white' : '#e8a878',
                  boxShadow: tab === t.key ? '0 4px 14px rgba(201,100,40,0.35)' : 'none',
                  transition: 'all 0.2s',
                }}>
                {t.label}
              </button>
            ))}
          </motion.div>
        </div>
      </div>

      {/* ── Tab Content ── */}
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '40px 20px' }}>
        <AnimatePresence mode="wait">

          {/* ── TRACK TAB ── */}
          {tab === 'track' && (
            <motion.div key="track" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>

              {/* Form */}
              <div style={{
                background: 'linear-gradient(135deg, #1a0f00, #0e0700)',
                borderRadius: 20, padding: 28, marginBottom: 24,
                border: '1px solid rgba(201,100,40,0.15)',
              }}>
                <form onSubmit={handleTrack} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ position: 'relative' }}>
                    <span style={{
                      position: 'absolute', left: 18, top: '50%', transform: 'translateY(-50%)',
                      color: '#c96428', fontWeight: 800, fontSize: 16, pointerEvents: 'none',
                    }}>#</span>
                    <input type="number" min="1" required placeholder="Order ID (e.g. 12)"
                      value={orderId} onChange={e => setOrderId(e.target.value)}
                      style={{ ...inputStyle, paddingLeft: 36 }} />
                  </div>
                  <input type="email" required placeholder="Email used while ordering"
                    value={trackEmail} onChange={e => setTrackEmail(e.target.value)}
                    style={inputStyle} />
                  <motion.button whileTap={{ scale: 0.97 }} type="submit" disabled={trackLoading}
                    style={{
                      padding: '16px 24px', borderRadius: 14, border: 'none',
                      cursor: trackLoading ? 'not-allowed' : 'pointer',
                      background: trackLoading ? '#6b3315' : 'linear-gradient(135deg, #c96428 0%, #a84e1e 100%)',
                      color: 'white', fontSize: 16, fontWeight: 700, fontFamily: 'inherit',
                      boxShadow: trackLoading ? 'none' : '0 6px 24px rgba(201,100,40,0.4)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 4,
                    }}>
                    {trackLoading ? 'Searching...' : '🔍 Track My Order'}
                  </motion.button>
                </form>
              </div>

              {/* Error */}
              <AnimatePresence>
                {trackError && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    style={{
                      marginBottom: 24, padding: '16px 20px', borderRadius: 16,
                      display: 'flex', alignItems: 'flex-start', gap: 12,
                      background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)',
                    }}>
                    <span style={{ fontSize: 22 }}>❌</span>
                    <div>
                      <p style={{ color: '#ef4444', fontWeight: 700, marginBottom: 2 }}>Order Not Found</p>
                      <p style={{ color: '#8b6347', fontSize: 13 }}>{trackError}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Order Result */}
              <AnimatePresence>
                {order && (
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                    {/* Status Banner */}
                    <div style={{
                      borderRadius: 20, padding: '28px 24px', textAlign: 'center',
                      background: `linear-gradient(135deg, ${statusInfo.color}12, ${statusInfo.color}06)`,
                      border: `1px solid ${statusInfo.color}30`,
                    }}>
                      <div style={{ fontSize: 52, marginBottom: 10 }}>{statusInfo.icon}</div>
                      <h2 className="font-display" style={{ fontSize: 24, fontWeight: 700, color: statusInfo.color, marginBottom: 6 }}>
                        {statusInfo.label}
                      </h2>
                      <p style={{ color: '#8b6347', fontSize: 14 }}>{statusInfo.desc}</p>
                    </div>

                    {/* Progress */}
                    {order.status !== 'cancelled' && (
                      <div style={{ background: 'white', borderRadius: 20, padding: 24, border: '1px solid #f0e6db', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
                        <p className="font-display" style={{ fontWeight: 700, color: '#1a0f00', marginBottom: 28, fontSize: 15 }}>Order Progress</p>
                        <div style={{ position: 'relative' }}>
                          <div style={{ position: 'absolute', top: 20, left: '5%', right: '5%', height: 2, background: '#f0e6db', borderRadius: 2 }} />
                          <div style={{
                            position: 'absolute', top: 20, left: '5%', height: 2,
                            background: 'linear-gradient(90deg, #c96428, #e07832)', borderRadius: 2,
                            transition: 'width 0.8s ease',
                            width: currentStepIndex >= 0 ? `${(currentStepIndex / (STATUS_STEPS.length - 1)) * 90}%` : '0%',
                          }} />
                          <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative' }}>
                            {STATUS_STEPS.map((step, i) => {
                              const done = i <= currentStepIndex;
                              const current = i === currentStepIndex;
                              return (
                                <div key={step} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                                  <motion.div animate={{ scale: current ? 1.2 : 1 }}
                                    style={{
                                      width: 40, height: 40, borderRadius: '50%', zIndex: 1, position: 'relative',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      fontSize: current ? 16 : 12, fontWeight: 700,
                                      background: done ? '#c96428' : '#f5ede0',
                                      color: done ? 'white' : '#b09070',
                                      border: current ? '3px solid #c96428' : '2px solid #e2d5c8',
                                      boxShadow: current ? '0 0 20px rgba(201,100,40,0.4)' : 'none',
                                    }}>
                                    {done ? (current ? STATUS_INFO[step]?.icon : '✓') : i + 1}
                                  </motion.div>
                                  <span style={{ fontSize: 10, fontWeight: current ? 700 : 500, color: done ? '#c96428' : '#b09070', textAlign: 'center', maxWidth: 56 }}>
                                    {STATUS_INFO[step]?.label.split(' ')[0]}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Order Details */}
                    <div style={{ background: 'white', borderRadius: 20, padding: 24, border: '1px solid #f0e6db', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <p className="font-display" style={{ fontSize: 18, fontWeight: 700, color: '#1a0f00' }}>Order #{order.order_number || order.id}</p>
                        <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 99, background: `${statusInfo.color}12`, color: statusInfo.color }}>
                          {statusInfo.label}
                        </span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
                        {[
                          ['Customer', order.customer_name],
                          ['Phone', order.phone],
                          ['Order Type', (order.order_type || 'dine-in').replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())],
                          ['Date & Time', formatDate(order.created_at)],
                        ].map(([label, value]) => (
                          <div key={label} style={{ background: '#fdf6f0', borderRadius: 12, padding: '12px 14px' }}>
                            <p style={{ color: '#8b6347', fontSize: 11, marginBottom: 3 }}>{label}</p>
                            <p style={{ color: '#1a0f00', fontWeight: 600, fontSize: 13 }}>{value}</p>
                          </div>
                        ))}
                      </div>
                      <div style={{ borderTop: '1px dashed #e2d5c8', paddingTop: 16 }}>
                        <p style={{ color: '#8b6347', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Items Ordered</p>
                        {(order.items || []).map((item, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid #f5ede0' }}>
                            <div>
                              <p style={{ color: '#1a0f00', fontWeight: 500, fontSize: 13 }}>{item.item_name}</p>
                              <p style={{ color: '#8b6347', fontSize: 11, marginTop: 2 }}>₹{parseFloat(item.price).toFixed(0)} × {item.quantity}</p>
                            </div>
                            <span style={{ color: '#c96428', fontWeight: 700, fontSize: 13 }}>₹{parseFloat(item.subtotal).toFixed(0)}</span>
                          </div>
                        ))}
                        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '2px solid #c96428', display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {parseFloat(order.discount_amount || 0) > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 10, background: '#dcfce7', border: '1px solid #a5d6a7' }}>
                              <span style={{ color: '#16a34a', fontSize: 14, fontWeight: 700 }}>🎟️ {order.coupon_code || 'Discount'}</span>
                              <span style={{ color: '#16a34a', fontSize: 14, fontWeight: 700 }}>-₹{parseFloat(order.discount_amount).toFixed(0)}</span>
                            </div>
                          )}
                          {(() => {
                            const itemsTotal = (order.items || []).reduce((s, i) => s + parseFloat(i.subtotal || i.price * i.quantity), 0);
                            const disc = parseFloat(order.discount_amount || 0);
                            const after = Math.max(itemsTotal - disc, 0);
                            const tax = gstSettings.enabled && gstSettings.rate > 0 ? (after * gstSettings.rate) / 100 : 0;
                            return (
                              <>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                  <span style={{ color: '#6b5344', fontSize: 14 }}>Subtotal</span>
                                  <span style={{ color: '#6b5344', fontSize: 14 }}>₹{after.toFixed(0)}</span>
                                </div>
                                {tax > 0 && (
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <span style={{ color: '#6b5344', fontSize: 14 }}>{gstSettings.label} ({gstSettings.rate}%)</span>
                                    <span style={{ color: '#c96428', fontSize: 14 }}>+₹{tax.toFixed(0)}</span>
                                  </div>
                                )}
                                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px dashed #e0cfc4', marginTop: 4 }}>
                                  <span className="font-display" style={{ fontWeight: 700, color: '#1a0f00', fontSize: 17 }}>Total Paid</span>
                                  <span style={{ fontWeight: 700, color: '#c96428', fontSize: 17 }}>₹{(after + tax).toFixed(0)}</span>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                      <button onClick={() => setShowReceipt(true)}
                        style={{
                          width: '100%', marginTop: 20, padding: '14px 24px', borderRadius: 14,
                          border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                          background: 'linear-gradient(135deg, #c96428, #a84e1e)',
                          color: 'white', fontSize: 15, fontWeight: 700,
                          boxShadow: '0 4px 16px rgba(201,100,40,0.3)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        }}>
                        📥 Download Receipt
                      </button>
                    </div>

                    <button onClick={() => { setOrder(null); setOrderId(''); setTrackEmail(''); setTrackError(''); }}
                      style={{
                        width: '100%', padding: '14px 24px', borderRadius: 14,
                        border: '1px solid #e2d5c8', background: 'white',
                        color: '#8b6347', fontSize: 14, fontWeight: 600,
                        fontFamily: 'inherit', cursor: 'pointer',
                      }}>
                      Track Another Order
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>

              {!order && !trackError && !trackLoading && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  style={{ textAlign: 'center', padding: '32px 24px' }}>
                  <div style={{ fontSize: 56, marginBottom: 14 }}>🔍</div>
                  <h3 className="font-display" style={{ fontSize: 20, fontWeight: 700, color: '#1a0f00', marginBottom: 8 }}>
                    Enter Your Order Details
                  </h3>
                  <p style={{ color: '#8b6347', fontSize: 14, lineHeight: 1.7 }}>
                    Your Order ID was shown after placing the order.<br />
                    Use the same email you entered while ordering.
                  </p>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* ── HISTORY TAB ── */}
          {tab === 'history' && (
            <motion.div key="history" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>

              {/* Form */}
              <div style={{
                background: 'linear-gradient(135deg, #1a0f00, #0e0700)',
                borderRadius: 20, padding: 28, marginBottom: 28,
                border: '1px solid rgba(201,100,40,0.15)',
              }}>
                <p style={{ color: '#e8a878', fontSize: 14, marginBottom: 16, textAlign: 'center' }}>
                  Enter the email you used while placing orders
                </p>
                <form onSubmit={handleHistory} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <input type="email" required placeholder="Your email address"
                    value={historyEmail} onChange={e => setHistoryEmail(e.target.value)}
                    style={inputStyle} />
                  <motion.button whileTap={{ scale: 0.97 }} type="submit" disabled={historyLoading}
                    style={{
                      padding: '16px 24px', borderRadius: 14, border: 'none',
                      cursor: historyLoading ? 'not-allowed' : 'pointer',
                      background: historyLoading ? '#6b3315' : 'linear-gradient(135deg, #c96428 0%, #a84e1e 100%)',
                      color: 'white', fontSize: 16, fontWeight: 700, fontFamily: 'inherit',
                      boxShadow: historyLoading ? 'none' : '0 6px 24px rgba(201,100,40,0.4)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                    }}>
                    {historyLoading ? 'Fetching...' : '📋 View My Orders'}
                  </motion.button>
                </form>
              </div>

              {/* Error */}
              <AnimatePresence>
                {historyError && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    style={{
                      marginBottom: 24, padding: '16px 20px', borderRadius: 16,
                      display: 'flex', alignItems: 'flex-start', gap: 12,
                      background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)',
                    }}>
                    <span style={{ fontSize: 22 }}>📭</span>
                    <div>
                      <p style={{ color: '#ef4444', fontWeight: 700, marginBottom: 2 }}>No Orders Found</p>
                      <p style={{ color: '#8b6347', fontSize: 13 }}>{historyError}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Results */}
              {historyOrders.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <p style={{ color: '#1a0f00', fontWeight: 700, fontSize: 16 }}>
                      {historyOrders.length} Order{historyOrders.length > 1 ? 's' : ''} Found
                    </p>
                    <p style={{ color: '#8b6347', fontSize: 13 }}>{historyEmail}</p>
                  </div>
                  {historyOrders.map(o => (
                    <HistoryCard key={o.id} order={o} onViewDetail={setSelectedOrder} />
                  ))}
                </div>
              )}

              {/* Empty state */}
              {!historySearched && historyOrders.length === 0 && !historyError && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  style={{ textAlign: 'center', padding: '32px 24px' }}>
                  <div style={{ fontSize: 56, marginBottom: 14 }}>📋</div>
                  <h3 className="font-display" style={{ fontSize: 20, fontWeight: 700, color: '#1a0f00', marginBottom: 8 }}>
                    View All Your Orders
                  </h3>
                  <p style={{ color: '#8b6347', fontSize: 14, lineHeight: 1.7 }}>
                    Enter your email to see your complete order history,<br />
                    view details, and download receipts.
                  </p>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Order Detail Modal */}
      <AnimatePresence>
        {selectedOrder && (
          <OrderDetailModal
            order={selectedOrder}
            gstSettings={gstSettings}
            onClose={() => setSelectedOrder(null)}
            slug={slug}
          />
        )}
      </AnimatePresence>

      {/* Receipt Modal (track tab) */}
      <AnimatePresence>
        {showReceipt && order && (
          <ReceiptGenerator order={order} slug={slug} onClose={() => setShowReceipt(false)} />
        )}
      </AnimatePresence>

      <style>{`
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input::placeholder { color: rgba(201,100,40,0.35) !important; }
        input:focus { border-color: rgba(201,100,40,0.6) !important; outline: none !important; box-shadow: 0 0 0 3px rgba(201,100,40,0.12) !important; }
      `}</style>
    </div>
  );
};

export default TrackOrder;
